

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, MapPin, DollarSign, Clock, Users, Video, Coins, Lock, Mail, Building2 } from 'lucide-react';
import { supabase } from '@/lib/supabase-client';
import { calculateTokensForDuration, formatMinutesWithTokens } from '@/lib/token-utils';
import { checkSufficientTokens } from '@/lib/tokens-api';
import { checkCandidateHasAccess, markInvitationAsAccepted, validateInvitationToken, linkUserToInvitation } from '@/lib/job-invitations';
import InvitationManager from '@/components/InvitationManager';

interface Job {
  id: string;
  title: string;
  company_name: string;
  description: string;
  requirements: string;
  tech_stack: string;
  location: string;
  salary_range: string;
  employment_type: string;
  status: string;
  created_at: string;
  interview_config_id: string;
  interview_configurations: any;
  user_id: string | null;
  duration_minutes?: number;
  organization_id: string | null;
  organizations?: {
    id: string;
    name: string;
    logo_url: string | null;
  };
}

export default function JobDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invite');

  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [startingInterview, setStartingInterview] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  const [hasEnoughTokens, setHasEnoughTokens] = useState(true);
  const [tokensShortfall, setTokensShortfall] = useState(0);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isRecruiter, setIsRecruiter] = useState(false);
  const [isOrgMember, setIsOrgMember] = useState(false);
  const [hasValidInvitationToken, setHasValidInvitationToken] = useState(false);

  useEffect(() => {
    if (params.id) {
      fetchJob();
    }
  }, [params.id]);

  const fetchJob = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
      setCurrentUserEmail(user?.email || null);

      let data = null;
      let error = null;

      // Try to fetch job normally first
      const normalFetch = await supabase
        .from('job_postings')
        .select(`
          *,
          interview_configurations (
            id,
            name,
            personality,
            interview_rules,
            interview_type,
            difficulty_level,
            max_duration_minutes,
            max_questions,
            default_questions,
            voice_gender,
            randomize_questions
          ),
          organizations (
            id,
            name,
            logo_url
          )
        `)
        .eq('id', params.id)
        .maybeSingle();

      // If normal fetch failed and user has invitation token, try with the function
      if (normalFetch.error && invitationToken && !user) {
        const { data: functionData, error: functionError } = await supabase.rpc(
          'get_job_with_invitation',
          {
            p_job_id: params.id,
            p_invitation_token: invitationToken,
          }
        );

        if (!functionError && functionData) {
          data = functionData;
          error = null;
        } else {
          error = normalFetch.error;
        }
      } else {
        data = normalFetch.data;
        error = normalFetch.error;
      }

      if (error) {
        console.error('Error fetching job:', error);
        setLoading(false);
        return;
      }

      setJob(data);

      // Check if invitation token is valid
      let tokenValid = false;
      if (invitationToken && data) {
        tokenValid = await validateInvitationToken(data.id, invitationToken);
        setHasValidInvitationToken(tokenValid);

        // If user is authenticated and token is valid, link them to the invitation
        if (user && user.email && tokenValid) {
          await linkUserToInvitation(invitationToken, user.email);
        }
      }

      // Check if user is authenticated and check access
      if (user && data) {
        // Check if user has access to this job
        if (data.organization_id) {
          const access = await checkCandidateHasAccess(data.id, user.email || '');
          setHasAccess(access || tokenValid);

          // Check if user is an organization member (recruiter)
          const { data: membership } = await supabase
            .from('organization_members')
            .select('role')
            .eq('user_id', user.id)
            .eq('organization_id', data.organization_id)
            .maybeSingle();

          setIsOrgMember(!!membership);

          // Check if user is a recruiter
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('is_recruiter')
            .eq('id', user.id)
            .maybeSingle();

          setIsRecruiter(profile?.is_recruiter || false);
        } else {
          // Public job, everyone has access
          setHasAccess(true);
        }

        // Check token balance
        const duration = data.interview_configurations?.max_duration_minutes || 5;
        const tokensRequired = calculateTokensForDuration(duration);
        try {
          const tokenCheck = await checkSufficientTokens(tokensRequired);
          setTokenBalance(tokenCheck.currentBalance);
          setHasEnoughTokens(tokenCheck.sufficient);
          setTokensShortfall(tokenCheck.shortfall);
        } catch (error) {
          console.error('Error checking tokens:', error);
        }
      } else if (data && data.organization_id) {
        // User not authenticated, but job is private
        // Allow viewing if they have a valid invitation token
        setHasAccess(tokenValid);
      } else {
        // Public job, no auth required for viewing
        setHasAccess(true);
      }
    } catch (error) {
      console.error('Error fetching job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartInterview = async () => {
    setStartingInterview(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        const returnUrl = invitationToken
          ? `/jobs/${params.id}?invite=${invitationToken}`
          : `/jobs/${params.id}`;
        navigate(`/signup?returnTo=${encodeURIComponent(returnUrl)}`);
        return;
      }

      if (!job) {
        alert('Job data not loaded');
        return;
      }

      // Check access for private jobs
      if (job.organization_id && !hasAccess && !hasValidInvitationToken) {
        alert(`This is a private job posting for ${job.organizations?.name || job.company_name}. You need an invitation to interview for this position.`);
        return;
      }

      // Link user to invitation if they have a valid token
      if (hasValidInvitationToken && invitationToken && user.email) {
        await linkUserToInvitation(invitationToken, user.email);
      }

      // Check tokens again before starting
      if (!hasEnoughTokens) {
        alert(`Insufficient tokens. You need ${tokensShortfall} more tokens to start this interview.`);
        navigate('/pricing');
        return;
      }

      // Calculate tokens to reserve
      const duration = job.interview_configurations?.max_duration_minutes || job.duration_minutes || 5;
      const tokensRequired = calculateTokensForDuration(duration);

      // Mark invitation as accepted if this is a private job
      if (job.organization_id && user.email) {
        await markInvitationAsAccepted(job.id, user.email);
      }

      // Create interview session with reserved tokens
      const { data: session, error: sessionError } = await supabase
        .from('interview_sessions')
        .insert({
          job_posting_id: params.id,
          interview_config_id: job.interview_config_id,
          user_id: user.id,
          candidate_name: user.email?.split('@')[0] || 'Candidate',
          candidate_email: user.email || '',
          status: 'started',
          started_at: new Date().toISOString(),
          tokens_reserved: tokensRequired,
        })
        .select()
        .single();

      if (sessionError || !session) {
        console.error('Session error:', sessionError);
        alert('Failed to create interview session');
        return;
      }

      // Store interview data
      const config = job.interview_configurations;
      if (config?.role_statement) {
        sessionStorage.setItem('interviewSystemPrompt', config.role_statement);
      }

      // Add voiceGender at top level for easy access
      const jobDataWithVoiceGender = {
        ...job,
        voiceGender: config?.voice_gender || 'male'
      };
      sessionStorage.setItem('interviewJobInfo', JSON.stringify(jobDataWithVoiceGender));

      if (config?.default_questions) {
        sessionStorage.setItem('interviewQuestions', JSON.stringify(config.default_questions));
      }

      const assistantIdParam = config?.assistant_id ? `&assistantId=${config.assistant_id}` : '';
      navigate(`/interview?sessionId=${session.id}${assistantIdParam}`);
    } catch (error) {
      console.error('Error starting interview:', error);
      alert('Error starting interview');
    } finally {
      setStartingInterview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-2xl font-bold">Job not found</h1>
          <Button onClick={() => navigate('/jobs')} className="mt-4">
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/jobs')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Jobs
          </Button>
          {job.user_id && job.user_id === currentUserId && (
            <Button
              onClick={() => navigate(`/jobs/${params.id}/edit`)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Edit Job
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-3xl">{job.title}</CardTitle>
                      {job.organization_id ? (
                        <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Private
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800">
                          Public
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {job.organizations?.name && (
                        <div className="flex items-center text-gray-600">
                          <Building2 className="w-4 h-4 mr-1" />
                          <span className="text-lg">{job.organizations.name}</span>
                        </div>
                      )}
                      {!job.organizations?.name && (
                        <p className="text-xl text-gray-700">{job.company_name}</p>
                      )}
                    </div>
                  </div>
                  <Badge className={
                    job.employment_type === 'full-time' ? 'bg-green-100 text-green-800' :
                    job.employment_type === 'contract' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }>
                    {job.employment_type}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  {job.location && (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      {job.location}
                    </div>
                  )}
                  {job.salary_range && (
                    <div className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-2" />
                      {job.salary_range}
                    </div>
                  )}
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Posted {new Date(job.created_at).toLocaleDateString()}
                  </div>
                </div>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Job Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{job.description}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{job.requirements}</p>
              </CardContent>
            </Card>

            {job.tech_stack && (
              <Card>
                <CardHeader>
                  <CardTitle>Tech Stack</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {job.tech_stack.split(',').map((tech, index) => (
                      <Badge key={index} variant="secondary">
                        {tech.trim()}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            {!isRecruiter && (
              <Card className="border-2 border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-green-900">
                    <Video className="w-5 h-5 mr-2" />
                    Start Interview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {hasValidInvitationToken && (
                      <Alert className="bg-blue-50 border-blue-200">
                        <Mail className="w-4 h-4 text-blue-700" />
                        <AlertDescription className="text-blue-800">
                          <strong>Invited</strong>
                          <p className="mt-1 text-sm">
                            {currentUserEmail ? (
                              `You're viewing this job via invitation link. You now have access to interview for this position.`
                            ) : (
                              `You're viewing this job via invitation link. Sign up or log in to start your interview.`
                            )}
                          </p>
                        </AlertDescription>
                      </Alert>
                    )}

                    {job.organization_id && hasAccess === false && !hasValidInvitationToken && (
                      <Alert className="bg-yellow-50 border-yellow-200">
                        <Lock className="w-4 h-4 text-yellow-700" />
                        <AlertDescription className="text-yellow-800">
                          <strong>Private Position</strong>
                          <p className="mt-1 text-sm">
                            {currentUserEmail ? (
                              `You need an invitation to interview for this ${job.organizations?.name || job.company_name} position. Please contact the recruiter if you believe you should have access.`
                            ) : (
                              `This is a private job posting. Please sign up or log in with an invited email address to start the interview.`
                            )}
                          </p>
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="bg-white p-3 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-600">Duration:</span>
                        <span className="font-semibold">{job.interview_configurations?.max_duration_minutes || 5} minutes</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-600">Tokens Required:</span>
                        <span className="font-semibold">{calculateTokensForDuration(job.interview_configurations?.max_duration_minutes || 5).toLocaleString()}</span>
                      </div>
                      {tokenBalance !== null && (
                        <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
                          <span className="text-gray-600">Your Balance:</span>
                          <span className={`font-semibold flex items-center ${hasEnoughTokens ? 'text-green-600' : 'text-red-600'}`}>
                            <Coins className="w-4 h-4 mr-1" />
                            {tokenBalance.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {!hasEnoughTokens && currentUserId && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-800">
                          You need {tokensShortfall.toLocaleString()} more tokens to start this interview.
                        </p>
                        <Button
                          className="w-full mt-2 bg-blue-600 hover:bg-blue-700"
                          onClick={() => navigate('/pricing')}
                          size="sm"
                        >
                          Buy Tokens
                        </Button>
                      </div>
                    )}

                    <p className="text-sm text-green-800">
                      {currentUserId ? (
                        'Ready to interview for this position? Click below to begin your AI-powered interview.'
                      ) : (
                        'Sign up or log in to start your interview for this position.'
                      )}
                    </p>
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={handleStartInterview}
                      disabled={Boolean(startingInterview || (currentUserId && (!hasEnoughTokens || (job.organization_id !== null && hasAccess === false && !hasValidInvitationToken))))}
                    >
                      {startingInterview ? 'Starting...' : currentUserId ? 'Interview for this Job' : 'Sign Up to Interview'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {isRecruiter && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    View Interviews
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(`/interviews?jobId=${job.id}`)}
                  >
                    View All Interviews
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {job.organization_id && isOrgMember && (
          <div className="mt-8">
            <InvitationManager jobId={job.id} jobTitle={job.title} />
          </div>
        )}
      </div>
    </div>
  );
}
